import React from "react";
import { Box, TextField, Typography } from "@mui/material";

import {
  COLORS,
  CustomDropdownField,
  EsgSingleSelectControlledField,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import type { InitiativeLevelType } from "api/initiatives/types";
import { InitiativeDetailOverviewIcon } from "assets";
import { LevelEntityField } from "pages/authenticated/accelerate/initiatives-management/components/create-initiative-modal/components";
import {
  LEVEL_OPTIONS,
  PRIORITY_COLORS,
} from "pages/authenticated/accelerate/initiatives-management/components/create-initiative-modal/consts";
import { FieldLabel } from "pages/authenticated/accelerate/initiatives-management/components/field-label";
import { detailMultiSelectSx, detailSelectWrapperSx } from "../../styles";

import { OverviewSectionProps } from "./types";
import { DEFAULT_TOOLTIPS } from "./consts";
import { CELL_FONT_SX } from "./styles";
import { SECTION_CARD_SX } from "../section-card/styles";

export const OverviewSection: React.FC<OverviewSectionProps> = ({
  form,
  updateField,
  categoryOptions,
  typeOptions,
  ownerOptions,
  allowedValues,
  tooltips = DEFAULT_TOOLTIPS,
}) => {
  const selectedOwners = ownerOptions.filter((o) =>
    form.ownerIds.includes(o.value),
  );

  return (
    <Box sx={SECTION_CARD_SX}>
      {/* ── Card header ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #F1F5F9",
          background: "linear-gradient(to right, #f8fafc, #ffffff, #f8fafc)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <InitiativeDetailOverviewIcon width={36} height={36} />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: "#1A4252", fontSize: "1.0625rem" }}
        >
          Overview
        </Typography>
      </Box>

      {/* ── Card body ── */}
      <Box sx={{ p: "28px", display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Metadata — 3-column grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "20px",
          }}
        >
          {/* Row 1: Category · Level · Site */}
          <Box sx={{ ...detailSelectWrapperSx, ...CELL_FONT_SX }}>
            <FieldLabel required tooltip={tooltips.category}>
              Category
            </FieldLabel>
            <CustomDropdownField
              label=""
              options={categoryOptions}
              selectedValue={form.category}
              placeholder="Select category..."
              onChange={(v) => updateField("category", v)}
              showCustomOption={false}
              sx={{ width: "100%" }}
            />
          </Box>

          <Box sx={{ ...detailSelectWrapperSx, ...CELL_FONT_SX }}>
            <FieldLabel tooltip={tooltips.level}>Level</FieldLabel>
            <EsgSingleSelectControlledField
              options={LEVEL_OPTIONS}
              selectedValue={form.level}
              setSelectedValue={(v) => {
                updateField("level", v as InitiativeLevelType);
                updateField("levelEntity", "");
              }}
              placeholder="Select level..."
              width="100%"
            />
          </Box>

          <Box
            sx={{
              ...detailSelectWrapperSx,
              ...CELL_FONT_SX,
              "& .MuiInputLabel-root": {
                fontSize: "11px",
                fontWeight: 600,
                color: "#475467",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                mb: "6px",
                lineHeight: 1,
                transform: "none",
                position: "static",
              },
            }}
          >
            <LevelEntityField
              level={form.level}
              value={form.levelEntity}
              onChange={(v) => updateField("levelEntity", v)}
              allowedValues={allowedValues}
              tooltip={tooltips.site}
            />
          </Box>

          {/* Row 2: Type · Owner · Priority */}
          <Box sx={{ ...detailSelectWrapperSx, ...CELL_FONT_SX }}>
            <FieldLabel required tooltip={tooltips.type}>
              Initiative Type
            </FieldLabel>
            <CustomDropdownField
              label=""
              options={typeOptions}
              selectedValue={form.type}
              placeholder="Select type..."
              onChange={(v) => updateField("type", v)}
              sx={{ width: "100%" }}
            />
          </Box>

          <Box sx={{ ...detailMultiSelectSx, ...CELL_FONT_SX }}>
            <FieldLabel tooltip={tooltips.owner}>Owner</FieldLabel>
            <EsgMultiselectControlledField
              options={ownerOptions}
              selectedValues={selectedOwners}
              setSelectedValues={(
                selected: Array<{ value: string | number }>,
              ) =>
                updateField(
                  "ownerIds",
                  selected.map((o) => String(o.value)),
                )
              }
            />
          </Box>

          <Box>
            <FieldLabel tooltip={tooltips.priority}>Priority</FieldLabel>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {(["LOW", "MEDIUM", "HIGH"] as const).map((p) => {
                const selected = form.priority === p;
                const colors = PRIORITY_COLORS[p];
                return (
                  <Box
                    key={p}
                    onClick={() => updateField("priority", p)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      px: 1.5,
                      height: "36px",
                      borderRadius: "30px",
                      border: `1px solid ${
                        selected ? colors.border : "#D0D5DD"
                      }`,
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: 600,
                      fontFamily: "Inter",
                      color: selected ? colors.text : "#8A94A6",
                      backgroundColor: selected ? colors.bg : "transparent",
                      transition:
                        "border-color 0.15s, color 0.15s, background-color 0.15s",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: colors.dot,
                        flexShrink: 0,
                      }}
                    />
                    {p.charAt(0) + p.slice(1).toLowerCase()}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Description — full width */}
        <Box sx={{ position: "relative" }}>
          <FieldLabel tooltip={tooltips.description}>Description</FieldLabel>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="Describe the initiative scope and objectives..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "14px",
                color: COLORS.Mainblue,
                "& fieldset": { borderColor: COLORS.GREY_BORDER },
                "&:hover fieldset": { borderColor: COLORS.GREY_BORDER },
                "&.Mui-focused fieldset": {
                  borderColor: COLORS.GREY_BORDER,
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "black",
                margin: "0",
                opacity: "0.4",
                fontSize: "12px",
                fontWeight: "500",
                fontStyle: "italic",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
